import { EventEmitter } from 'events';
import { InMemoryStorage } from './InMemoryStorage';


export const LOCAL_STORAGE_KEYS = {
  token: 'token',
  refreshToken: 'refreshToken',
};


const getStorage = (): Storage => {
  try {
    window.localStorage.setItem('__storage', '');
    window.localStorage.removeItem('__storage');
    return window.localStorage;
  } catch (err) {
    console.warn('"localStorage" is not available');
    return new InMemoryStorage();
  }
};


export const localStorage = getStorage();


export class LocalStorageService {
  // TODO: make this value constant without ability to change
  private static appNameSpace = '';
  private static emitter: EventEmitter = new EventEmitter();

  static setAppNameSpace(appNameSpace: string) {
    LocalStorageService.appNameSpace = appNameSpace;
  }

  static getKey(key: string) {
    return `${LocalStorageService.appNameSpace}-${key}`;
  }

  static setItem(key: string, value: unknown): void {
    try {
      const valueAsString = JSON.stringify(value ?? null);
      const fullKey = LocalStorageService.getKey(key);

      localStorage.setItem(fullKey, valueAsString);
    } catch (e) {
      console.error(e);
    }
  }

  static serializeData<T = string>(valueFromStorage?: string | null): T | undefined {
    if (valueFromStorage === undefined) {
      return undefined;
    }

    try {
      return JSON.parse(valueFromStorage);
    } catch (e) {
      console.error(e);
    }
  }

  static getItem<T = string>(key: string): T | undefined {
    const fullKey = LocalStorageService.getKey(key);
    const valueFromStorage = localStorage.getItem(fullKey);

    return LocalStorageService.serializeData(valueFromStorage);
  }

  static removeItem(key: string): void {
    const fullKey = LocalStorageService.getKey(key);

    return localStorage.removeItem(fullKey);
  }

  static clear(): void {
    Object.keys(localStorage).forEach((key: string) => {
      if (key.includes(LocalStorageService.appNameSpace)) {
        localStorage.removeItem(key);
      }
    });
  }

  //
  // Storage events handling
  //
  private static handleStorageEvent = (e: StorageEvent) => {
    if (!e.key?.startsWith(LocalStorageService.appNameSpace)) {
      return;
    }

    LocalStorageService.emitter.emit(
      e.key,
      LocalStorageService.serializeData(e.newValue),
      LocalStorageService.serializeData(e.oldValue)
    );
  };

  static listenStorageEvents() {
    window.addEventListener('storage', LocalStorageService.handleStorageEvent);
  }

  static addValueChangeListener<T>(key: string, callback: (newValue: T, oldValue?: T) => void) {
    LocalStorageService.emitter.addListener(LocalStorageService.getKey(key), callback);
  }

  static removeValueChangeListener<T>(key: string, callback: (newValue: T, oldValue?: T) => void) {
    LocalStorageService.emitter.removeListener(LocalStorageService.getKey(key), callback);
  }

  static setItemWithEmit(_key: string, value: unknown) {
    const key = LocalStorageService.getKey(_key);
    const oldRawValue = localStorage.getItem(key);

    LocalStorageService.setItem(_key, value);
    const newRawValue = localStorage.getItem(key);

    if (newRawValue !== oldRawValue) {
      LocalStorageService.emitter.emit(
        key,
        LocalStorageService.serializeData(newRawValue),
        LocalStorageService.serializeData(oldRawValue)
      );
    }
  }
}

LocalStorageService.listenStorageEvents();
